import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DomioLogo from '../common/DomioLogo';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useI18n } from '../../i18n';
import { Menu, X } from 'lucide-react';

/** Public marketing pages: landing, pricing, etc. */
export default function MarketingLayout({ children }) {
  const { t } = useI18n();
  const location = useLocation();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navLinks = useMemo(
    () => [
      { label: t('landing.nav.features'), href: '/#features' },
      { label: t('landing.nav.solutions'), href: '/#solutions' },
      { label: t('landing.nav.about'), href: '/#about' },
      { label: t('landing.nav.pricing'), href: '/pricing' },
      { label: t('landing.nav.contact'), href: '/#contact' },
    ],
    [t],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenu(false);
  }, [location.pathname]);

  const isPricing = location.pathname === '/pricing';
  const navSolid = scrolled || isPricing;

  const linkClass = (href) => {
    const active = href === '/pricing' && isPricing;
    return [
      'text-sm font-medium transition-colors hover:text-domio-green',
      navSolid ? 'text-slate-700' : 'text-white/80',
      active ? '!text-domio-green font-semibold' : '',
    ].join(' ');
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-figtree">
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          navSolid ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/">
            <DomioLogo
              size="md"
              showText
              textClassName={navSolid ? 'text-slate-900' : 'text-white'}
              trailing={
                <LanguageSwitcher
                  dark={!navSolid}
                  inline={navSolid}
                  className="shrink-0 hidden sm:flex"
                />
              }
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((l) =>
              l.href.startsWith('/#') ? (
                <a key={l.href} href={l.href} className={linkClass(l.href)}>
                  {l.label}
                </a>
              ) : (
                <Link key={l.href} to={l.href} className={linkClass(l.href)}>
                  {l.label}
                </Link>
              ),
            )}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className={`text-sm font-medium px-4 py-2 rounded-lg transition ${
                navSolid ? 'text-slate-700 hover:text-domio-600' : 'text-white hover:text-domio-green'
              }`}
            >
              {t('landing.nav.logIn')}
            </Link>
            <Link
              to="/register"
              className="text-sm font-bold px-5 py-2.5 bg-domio-green text-white rounded-lg hover:bg-domio-green-dark transition shadow-lg shadow-domio-green/25"
            >
              {t('landing.nav.getStarted')}
            </Link>
          </div>
          <button
            type="button"
            className="md:hidden"
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label={t('common.menu')}
          >
            {mobileMenu ? (
              <X className={`h-6 w-6 ${navSolid ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${navSolid ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t shadow-lg p-4 space-y-3">
            {navLinks.map((l) =>
              l.href.startsWith('/#') ? (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileMenu(false)}
                  className="block text-slate-700 font-medium py-2"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setMobileMenu(false)}
                  className={`block font-medium py-2 ${isPricing && l.href === '/pricing' ? 'text-domio-green' : 'text-slate-700'}`}
                >
                  {l.label}
                </Link>
              ),
            )}
            <div className="py-1">
              <LanguageSwitcher inline />
            </div>
            <div className="flex gap-3 pt-2">
              <Link
                to="/login"
                className="flex-1 text-center text-sm font-medium py-2.5 border border-slate-300 rounded-lg"
              >
                {t('landing.nav.logIn')}
              </Link>
              <Link
                to="/register"
                className="flex-1 text-center text-sm font-bold py-2.5 bg-domio-green text-white rounded-lg"
              >
                {t('landing.nav.getStarted')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {children}

      <footer className="bg-[#071E35] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/">
              <DomioLogo size="md" showText textClassName="text-white" />
            </Link>
            <div className="flex gap-6 text-sm text-slate-400 flex-wrap justify-center">
              {navLinks.map((l) =>
                l.href.startsWith('/#') ? (
                  <a key={l.href} href={l.href} className="hover:text-white transition">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.href} to={l.href} className="hover:text-white transition">
                    {l.label}
                  </Link>
                ),
              )}
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} Domio. {t('landing.footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
